import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Button } from "../../general/Button";
import { Typography } from "../../general/Typography";

interface OTPProps {
  length?: number;
  onSubmit: (otp: string) => void;
  isLoading?: boolean;
  error?: string;
  resendOTP?: () => void;
}

const OTPContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
  max-width: 350px;
  position: relative;
  cursor: text;
`;

const OTPInputGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const OTPInputBox = styled.div<{ active?: boolean }>`
  width: 50px;
  height: 50px;
  font-size: ${({ theme }) => theme.typography.heading2.fontSize};
  text-align: center;
  border: 0.05px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ active, theme }) =>
    active &&
    `border-color: ${theme.colors.primaryHover}; box-shadow: 0 0 0 1px ${theme.colors.primary};`}

  user-select: none;
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 100%;
  top: 0;
  left: 0;
`;

export const OTP: React.FC<OTPProps> = ({
  length = 6,
  onSubmit,
  isLoading,
  error,
  resendOTP,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const [resendTimer, setResendTimer] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const focusHiddenInput = () => inputRef.current?.focus();

  // Autofocus hidden input on mount
  useEffect(() => {
    focusHiddenInput();
  }, []);

  // Handle Web OTP API autofill (for SMS)
  useEffect(() => {
    if ("OTPCredential" in window) {
      const ac = new AbortController();
      navigator.credentials
        .get({
          otp: { transport: ["sms"] },
          signal: ac.signal,
        } as any)
        .then((content: any) => {
          if (content?.code) {
            const chars = content.code.split("").slice(0, length);
            setOtp(chars);
            onSubmit(content.code);
          }
        })
        .catch(console.error);

      return () => ac.abort();
    }
  }, []);

  // Auto-submit if complete
  useEffect(() => {
    const joined = otp.join("");
    if (joined.length === length && !otp.includes("")) {
      onSubmit(joined);
    }
  }, [otp]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, length);
    const chars = raw.split("");
    const padded = [...chars, ...Array(length - chars.length).fill("")];
    setOtp(padded);
  };

  const handleResend = () => {
    resendOTP?.();
    setResendTimer(30);
  };

  useEffect(() => {
    if (resendTimer === 0) return;
    const timer = setInterval(() => {
      setResendTimer((t) => Math.max(t - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendTimer]);

  const firstEmptyIndex = otp.findIndex((d) => d === "");

  return (
    <OTPContainer onClick={focusHiddenInput}>
      <OTPInputGroup>
        {otp.map((digit, index) => (
          <OTPInputBox
            key={index}
            tabIndex={-1}
            aria-hidden="true"
            onClick={focusHiddenInput}
            active={index === (firstEmptyIndex === -1 ? length - 1 : firstEmptyIndex)}
          >
            {digit}
          </OTPInputBox>
        ))}
      </OTPInputGroup>

      <HiddenInput
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={otp.join("")}
        onChange={handleChange}
      />

      {error && (
        <Typography variant="error" colorScheme="error">
          {error}
        </Typography>
      )}

      <Button
        fullWidth
        isLoading={isLoading}
        disabled={otp.join("").length !== length}
        onClick={() => onSubmit(otp.join(""))}
      >
        Verify
      </Button>

      <Typography variant="caption">
        Didn’t receive the code?{" "}
        <button
          onClick={handleResend}
          disabled={resendTimer > 0}
          style={{
            background: "none",
            border: "none",
            color: "blue",
            cursor: resendTimer > 0 ? "not-allowed" : "pointer",
          }}
        >
          {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend"}
        </button>
      </Typography>
    </OTPContainer>
  );
};